import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';

import {
  AlertComponent,
  DisplayInputComponent,
  VehicleInsuranceDialog,
} from '@components';
import { User, Vehicle, VehicleInsurance } from '@models';
import { UtilService } from '@services';
import {
  assignUserToVehicle,
  assignUserToVehicleFailed,
  assignUserToVehicleSuccess,
  fetchCurrentVehicle,
  fetchCurrentVehicleInsurance,
  fetchUsers,
  removeUserFromVehicle,
  removeUserFromVehicleFailed,
  removeUserFromVehicleSuccess,
  selectCurrentVehicle,
  selectCurrentVehicleInsurance,
  selectCurrentVehicleInsuranceFetchedAt,
  selectUsers,
  selectUsersElegibleForVehicle,
  selectVehiclesFetchedAt,
} from '@state';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    DisplayInputComponent,
    AlertComponent,
  ],
  templateUrl: './vehicle.page.html',
  styleUrl: './vehicle.page.scss',
})
export class VehiclePage implements OnInit, OnDestroy {
  public $vehicle = this.store.select(selectCurrentVehicle);
  public $users = this.store.select(selectUsersElegibleForVehicle);
  public $vehiclesFetchedAt = this.store.select(selectVehiclesFetchedAt);

  public $vehicleInsurance = this.store.select(selectCurrentVehicleInsurance);
  public $vehicleInsuranceFetchedAt = this.store.select(
    selectCurrentVehicleInsuranceFetchedAt
  );

  userId = '';
  public loading: boolean = false;
  public error: string | null = null;

  private allUsers: User[] = [];
  private sub: Subscription | null = null;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private utils: UtilService
  ) {}

  public ngOnInit(): void {
    this.store.dispatch(fetchCurrentVehicle());
    this.store.dispatch(fetchCurrentVehicleInsurance());
    this.store.dispatch(fetchUsers());

    this.sub = this.store.select(selectUsers).subscribe((users) => {
      this.allUsers = users;
    });
  }

  public ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  public async onAssignDriverClicked(vehicleId: string, userId: string) {
    if (!userId || !vehicleId) {
      this.error = 'Please select a driver to assign to the vehicle.';
      return;
    }

    this.error = '';
    this.loading = true;
    try {
      await this.utils.actionsToPromise(
        assignUserToVehicle({ vehicleId, userId }),
        assignUserToVehicleSuccess,
        assignUserToVehicleFailed
      );
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }
    this.loading = false;
  }

  public async onRemoveDriverClicked(vehicle: Vehicle) {
    if (!vehicle.id || !vehicle?.user?.id) {
      this.error = 'Cannot remove driver from vehicle.';
      return;
    }

    this.error = '';
    this.loading = true;
    try {
      const payload = { vehicleId: vehicle.id, userId: vehicle.user.id };

      await this.utils.actionsToPromise(
        removeUserFromVehicle(payload),
        removeUserFromVehicleSuccess,
        removeUserFromVehicleFailed
      );
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }
    this.loading = false;
  }

  public async onVehicleInsuranceClicked(
    vehicle: Vehicle,
    vehicleInsurance: VehicleInsurance,
    users: User[]
  ) {
    const user = this.allUsers.find((user) => user.id === vehicle.userId);
    this.dialog.open(VehicleInsuranceDialog, {
      width: '800px',
      maxWidth: '800px',
      maxHeight: '90vh',
      data: {
        user,
        vehicle,
        vehicleInsurance,
      },
    });
  }
}
